/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../../hooks/common/use-application-state'
import { useTranslatedText } from '../../../hooks/common/use-translated-text'
import { cypressId } from '../../../utils/cypress-attribute'
import { buildNoteTree, countNotesInFolder, findFolder } from '../../note-tree/build-note-tree'
import { UiIcon } from '../icons/ui-icon'
import styles from './folder-path-picker.module.scss'
import {
  isTopNotePath,
  NOTE_PATH_MAX_DEPTH,
  NOTE_PATH_MAX_SEGMENT_LENGTH,
  NOTE_PATH_SEPARATOR,
  splitNotePath,
  TOP_NOTE_PATH
} from '@hedgedoc/commons'
import type { ChangeEvent, KeyboardEvent } from 'react'
import React, { Fragment, useCallback, useMemo, useState } from 'react'
import { Badge, Button, Form, ListGroup } from 'react-bootstrap'
import {
  ArrowUpLeft as IconUp,
  Folder as IconFolder,
  HouseDoor as IconRoot,
  Pin as IconTop
} from 'react-bootstrap-icons'
import { Trans } from 'react-i18next'

export interface FolderPathPickerProps {
  /** The currently selected path */
  value: string
  onChange: (path: string) => void
  disabled?: boolean
}

const joinPath = (segments: string[]): string => segments.join(NOTE_PATH_SEPARATOR)

/**
 * Lets the user choose the path of a page by drilling down through the existing folders.
 * The current path is shown as a breadcrumb. Typing a name filters the sub folders and creates a new folder on enter.
 *
 * @param value The currently selected path
 * @param onChange Is called with the new path
 * @param disabled Whether the path can't be changed
 */
export const FolderPathPicker: React.FC<FolderPathPickerProps> = ({ value, onChange, disabled }) => {
  const entries = useApplicationState((state) => state.noteTree.entries)
  const tree = useMemo(() => buildNoteTree(Object.values(entries)), [entries])
  const [input, setInput] = useState('')
  const [textMode, setTextMode] = useState(false)

  const isTop = isTopNotePath(value)
  const segments = useMemo(() => (isTop ? [] : splitNotePath(value)), [isTop, value])
  const currentFolder = useMemo(() => findFolder(tree, joinPath(segments)), [tree, segments])
  const isRoot = segments.length === 0 && !isTop
  const maxDepthReached = segments.length >= NOTE_PATH_MAX_DEPTH
  const isNewFolder = !isRoot && !isTop && currentFolder === undefined

  const subFolders = useMemo(() => {
    const folders = isRoot ? tree.folders : (currentFolder?.folders ?? [])
    const filter = input.trim().toLowerCase()
    return filter === '' ? folders : folders.filter((folder) => folder.name.toLowerCase().includes(filter))
  }, [currentFolder, input, isRoot, tree])

  const selectPath = useCallback(
    (newSegments: string[]) => {
      setInput('')
      onChange(joinPath(newSegments))
    },
    [onChange]
  )
  const selectRoot = useCallback(() => selectPath([]), [selectPath])
  const selectTop = useCallback(() => {
    setInput('')
    onChange(TOP_NOTE_PATH)
  }, [onChange])
  const goUp = useCallback(() => selectPath(segments.slice(0, -1)), [segments, selectPath])

  const addSegment = useCallback(() => {
    const name = input.trim().slice(0, NOTE_PATH_MAX_SEGMENT_LENGTH).trim()
    if (name === '' || maxDepthReached) {
      return
    }
    // Reuse the spelling of an existing folder, so that no duplicate with a different case is created
    const siblings = isRoot ? tree.folders : (currentFolder?.folders ?? [])
    const existing = siblings.find((folder) => folder.name.toLowerCase() === name.toLowerCase())
    selectPath([...segments, existing?.name ?? name])
  }, [currentFolder, input, isRoot, maxDepthReached, segments, selectPath, tree])

  const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.value.replaceAll(NOTE_PATH_SEPARATOR, ''))
  }, [])

  const onInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === NOTE_PATH_SEPARATOR) {
        // Enter would submit the surrounding form
        event.preventDefault()
        addSegment()
      } else if (event.key === 'Backspace' && input === '' && (segments.length > 0 || isTop)) {
        event.preventDefault()
        selectPath(segments.slice(0, -1))
      }
    },
    [addSegment, input, isTop, segments, selectPath]
  )

  const onTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value),
    [onChange]
  )
  const toggleTextMode = useCallback(() => setTextMode((old) => !old), [])

  const rootLabel = useTranslatedText('pageSettings.path.root')
  const newFolderLabel = useTranslatedText('pageSettings.path.newFolder')
  const pathPlaceholder = useTranslatedText('pageSettings.path.placeholder')

  if (textMode) {
    return (
      <Fragment>
        <Form.Control
          type={'text'}
          value={value}
          onChange={onTextChange}
          placeholder={pathPlaceholder}
          isInvalid={splitNotePath(value).length > NOTE_PATH_MAX_DEPTH}
          disabled={disabled}
          maxLength={512}
          {...cypressId('page-settings-path')}
        />
        <Form.Control.Feedback type={'invalid'}>
          <Trans i18nKey={'pageSettings.path.tooDeep'} values={{ maxDepth: NOTE_PATH_MAX_DEPTH }} />
        </Form.Control.Feedback>
        <Button variant={'link'} size={'sm'} className={'px-0'} onClick={toggleTextMode}>
          <Trans i18nKey={'pageSettings.path.useBrowser'} />
        </Button>
      </Fragment>
    )
  }

  return (
    <div {...cypressId('folder-path-picker')}>
      <div className={`form-control d-flex flex-wrap align-items-center gap-1 ${styles.breadcrumb}`}>
        <button
          type={'button'}
          className={styles.segment}
          onClick={selectRoot}
          disabled={disabled}
          title={rootLabel}
          aria-label={rootLabel}>
          <UiIcon icon={IconRoot} />
        </button>
        {isTop && (
          <Fragment>
            <span className={styles.separator}>/</span>
            <span className={`${styles.segment} ${styles.current}`}>
              <UiIcon icon={IconTop} className={'me-1'} />
              {TOP_NOTE_PATH}
            </span>
          </Fragment>
        )}
        {segments.map((segment, index) => (
          <Fragment key={index}>
            <span className={styles.separator}>/</span>
            <button
              type={'button'}
              className={`${styles.segment} ${index === segments.length - 1 ? styles.current : ''}`}
              onClick={() => selectPath(segments.slice(0, index + 1))}
              disabled={disabled}>
              {segment}
            </button>
          </Fragment>
        ))}
        {!isTop && !maxDepthReached && (
          <Fragment>
            <span className={styles.separator}>/</span>
            <input
              className={styles.input}
              value={input}
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              placeholder={newFolderLabel}
              aria-label={newFolderLabel}
              disabled={disabled}
              maxLength={NOTE_PATH_MAX_SEGMENT_LENGTH}
              {...cypressId('folder-path-picker-input')}
            />
          </Fragment>
        )}
      </div>
      <ListGroup className={`mt-1 ${styles.list}`}>
        {!isRoot && (
          <ListGroup.Item action as={'button'} type={'button'} onClick={goUp} disabled={disabled}>
            <UiIcon icon={IconUp} className={'me-2'} />
            <Trans i18nKey={'pageSettings.path.up'} />
          </ListGroup.Item>
        )}
        {!isTop &&
          !maxDepthReached &&
          subFolders.map((folder) => (
            <ListGroup.Item
              key={folder.path}
              action
              as={'button'}
              type={'button'}
              className={'d-flex align-items-center'}
              onClick={() => selectPath([...segments, folder.name])}
              disabled={disabled}>
              <UiIcon icon={IconFolder} className={'me-2'} />
              <span className={'flex-fill text-truncate text-start'}>{folder.name}</span>
              <Badge bg={'secondary'} pill>
                {countNotesInFolder(folder)}
              </Badge>
            </ListGroup.Item>
          ))}
        {isRoot && input.trim() === '' && (
          <ListGroup.Item action as={'button'} type={'button'} onClick={selectTop} disabled={disabled}>
            <UiIcon icon={IconTop} className={'me-2'} />
            <Trans i18nKey={'pageSettings.path.top'} />
          </ListGroup.Item>
        )}
        {input.trim() !== '' && !maxDepthReached && (
          <ListGroup.Item action as={'button'} type={'button'} onClick={addSegment} disabled={disabled}>
            <UiIcon icon={IconFolder} className={'me-2'} />
            <Trans i18nKey={'pageSettings.path.createFolder'} values={{ name: input.trim() }} />
          </ListGroup.Item>
        )}
      </ListGroup>
      {isNewFolder && (
        <Form.Text className={'d-block'}>
          <Trans i18nKey={'pageSettings.path.newFolderHint'} />
        </Form.Text>
      )}
      {maxDepthReached && (
        <Form.Text className={'d-block'}>
          <Trans i18nKey={'pageSettings.path.maxDepthReached'} values={{ maxDepth: NOTE_PATH_MAX_DEPTH }} />
        </Form.Text>
      )}
      <Button variant={'link'} size={'sm'} className={'px-0'} onClick={toggleTextMode} disabled={disabled}>
        <Trans i18nKey={'pageSettings.path.editAsText'} />
      </Button>
    </div>
  )
}
